import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Line } from "react-chartjs-2";
import "chart.js/auto";

const VitalSignsHistory = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [days, setDays] = useState(7);

  const {
    data: patientsData,
    isLoading: patientsLoading,
  } = useQuery({
    queryKey: ["nurse-patients"],
    queryFn: () =>
      axios
        .get(`${process.env.REACT_APP_API_URL}/nurse-patients/`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => res.data),
    enabled: !!token,
  });

  const patientId = patient?.patient_id || patient?.id || selectedPatient?.patient_id || selectedPatient?.id;

  const {
    data: trendsData,
    isLoading: trendsLoading,
    isError: trendsError,
  } = useQuery({
    queryKey: ["vital-signs-trends", patientId, days],
    queryFn: () =>
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/vital-signs/trends/?patient=${patientId}&days=${days}`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        )
        .then((res) => res.data),
    enabled: !!token && !!patientId,
  });

  const allPatients = [
    ...(patientsData?.ipd_patients || []),
    ...(patientsData?.icu_patients || []),
  ];

  const buildChartData = (label, data, color, unit = "") => {
    return {
      labels: trendsData?.dates || [],
      datasets: [
        {
          label: `${label}${unit ? ` (${unit})` : ""}`,
          data: data || [],
          borderColor: color,
          backgroundColor: `${color}20`,
          tension: 0.4,
          fill: true,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
    },
    scales: {
      y: {
        beginAtZero: false,
      },
    },
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5">
              {t("nurse.vitalSignsHistory", "Vital Signs History & Trends")}
            </Typography>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>{t("nurse.timeRange", "Time Range")}</InputLabel>
              <Select
                value={days}
                label={t("nurse.timeRange", "Time Range")}
                onChange={(e) => setDays(e.target.value)}
              >
                <MenuItem value={1}>{t("nurse.lastDay", "Last 24 Hours")}</MenuItem>
                <MenuItem value={3}>{t("nurse.last3Days", "Last 3 Days")}</MenuItem>
                <MenuItem value={7}>{t("nurse.lastWeek", "Last Week")}</MenuItem>
                <MenuItem value={14}>{t("nurse.last2Weeks", "Last 2 Weeks")}</MenuItem>
                <MenuItem value={30}>{t("nurse.lastMonth", "Last Month")}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={allPatients}
              getOptionLabel={(option) => option.patient_name || ""}
              value={selectedPatient}
              onChange={(event, newValue) => {
                setSelectedPatient(newValue);
              }}
              inputValue={patientSearch}
              onInputChange={(event, newInputValue) => {
                setPatientSearch(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("nurse.selectPatient", "Select Patient")}
                  placeholder={t("nurse.searchPatient", "Search patient...")}
                />
              )}
            />
          </Box>

          {(selectedPatient || patient) && (
            <>
              {trendsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : trendsError ? (
                <Alert severity="error">
                  {t("nurse.errorLoadingTrends", "Failed to load vital signs trends.")}
                </Alert>
              ) : trendsData && trendsData.dates && trendsData.dates.length > 0 ? (
                <Grid container spacing={2}>
                  {trendsData.temperature && trendsData.temperature.some(v => v !== null) && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("nurse.temperature", "Temperature")} (°C)
                          </Typography>
                          <Box sx={{ height: 250 }}>
                            <Line
                              data={buildChartData(
                                t("nurse.temperature", "Temperature"),
                                trendsData.temperature,
                                "#FF6384",
                                "°C"
                              )}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {trendsData.heart_rate && trendsData.heart_rate.some(v => v !== null) && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("nurse.heartRate", "Heart Rate")} (bpm)
                          </Typography>
                          <Box sx={{ height: 250 }}>
                            <Line
                              data={buildChartData(
                                t("nurse.heartRate", "Heart Rate"),
                                trendsData.heart_rate,
                                "#36A2EB",
                                "bpm"
                              )}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {trendsData.blood_pressure_systolic && trendsData.blood_pressure_systolic.some(v => v !== null) && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("nurse.bloodPressure", "Blood Pressure")} (mmHg)
                          </Typography>
                          <Box sx={{ height: 250 }}>
                            <Line
                              data={{
                                labels: trendsData.dates || [],
                                datasets: [
                                  {
                                    label: t("nurse.systolic", "Systolic"),
                                    data: trendsData.blood_pressure_systolic || [],
                                    borderColor: "#FF6384",
                                    backgroundColor: "#FF638420",
                                    tension: 0.4,
                                  },
                                  {
                                    label: t("nurse.diastolic", "Diastolic"),
                                    data: trendsData.blood_pressure_diastolic || [],
                                    borderColor: "#36A2EB",
                                    backgroundColor: "#36A2EB20",
                                    tension: 0.4,
                                  },
                                ],
                              }}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {trendsData.respiratory_rate && trendsData.respiratory_rate.some(v => v !== null) && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("nurse.respiratoryRate", "Respiratory Rate")} (bpm)
                          </Typography>
                          <Box sx={{ height: 250 }}>
                            <Line
                              data={buildChartData(
                                t("nurse.respiratoryRate", "Respiratory Rate"),
                                trendsData.respiratory_rate,
                                "#4BC0C0",
                                "bpm"
                              )}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {trendsData.oxygen_saturation && trendsData.oxygen_saturation.some(v => v !== null) && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("nurse.oxygenSaturation", "Oxygen Saturation")} (%)
                          </Typography>
                          <Box sx={{ height: 250 }}>
                            <Line
                              data={buildChartData(
                                t("nurse.oxygenSaturation", "Oxygen Saturation"),
                                trendsData.oxygen_saturation,
                                "#9966FF",
                                "%"
                              )}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {trendsData.blood_glucose && trendsData.blood_glucose.some(v => v !== null) && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("nurse.bloodGlucose", "Blood Glucose")} (mg/dL)
                          </Typography>
                          <Box sx={{ height: 250 }}>
                            <Line
                              data={buildChartData(
                                t("nurse.bloodGlucose", "Blood Glucose"),
                                trendsData.blood_glucose,
                                "#FF9F40",
                                "mg/dL"
                              )}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {trendsData.pain_score && trendsData.pain_score.some(v => v !== null) && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("nurse.painScore", "Pain Score")} (0-10)
                          </Typography>
                          <Box sx={{ height: 250 }}>
                            <Line
                              data={buildChartData(
                                t("nurse.painScore", "Pain Score"),
                                trendsData.pain_score,
                                "#FF6384",
                                "/10"
                              )}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Alert severity="info">
                  {t("nurse.noVitalSignsData", "No vital signs data available for the selected time range.")}
                </Alert>
              )}
            </>
          )}

          {!selectedPatient && !patient && (
            <Alert severity="info">
              {t("nurse.selectPatientToViewTrends", "Please select a patient to view vital signs trends.")}
            </Alert>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default VitalSignsHistory;
